import { observer } from "mobx-react-lite";
import { USER_ROLES } from "./shared/functions/CONSTANTS";
import PrimeReact from "primereact/api";
import React from "react";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Loading from "./shared/components/loading/Loading";
import SnackbarManager from "./shared/components/snackbar/SnackbarManager";
import { AppContext, useAppContext } from "./shared/functions/Context";
import Dashboard from "./logged-in/dashboard/Dashboard";
import useNetwork from "./shared/hooks/useNetwork";
import { MainApp } from "./shared/models/App";
import routes from "./logged-in/Routes/projectmanagement.route";
import FeedbackRoutes from "./logged-in/Routes/feedback.route";
import RegisterUserForm from "./logged-in/login/ResetPasswordForm";
import { BonusReport } from "./logged-in/reports/bonus-report/BonusReport";

//360
import Main360 from "./logged-in/360-feedback/Main360";
import MainAdmin from "./logged-in/360-feedback/MainAdmin";

import FeedbackReviewHome from "./logged-in/360-feedback/feed-back-review/FeedBackReviewHome";
import Questionnaire from "./logged-in/360-feedback/questionnaire/questionnaire";
import FeedbackReview360 from "./logged-in/360-feedback/feed-back-review/FeedbackReview360";
import UserRequestsRating from "./logged-in/360-feedback/questionnaire/views/UserRequestsRating";
import DisputeHome from "./logged-in/dispute-scorecards/DisputeHome";
import PersonalDevelopmentHome from "./logged-in/Personal-development/PersonalDevelopmentHome";
import DisputeEmployeeScorecardView from "./logged-in/dispute-scorecards/DisputeEmployeeScorecardView";
import ResolveEmployeeDisputeScorecardView from "./logged-in/dispute-scorecards/ResolveEmployeeDisputeScorecardView";
import SupervisorDisputeView from "./logged-in/dispute-scorecards/supervisor-dispute-view";
import Email from "./logged-in/emails/Email";
import QueryData from "./logged-in/admin-settings/QueryData";

const IndividualScorecard = React.lazy(
  () => import("./logged-in/execution-scorecard/IndividualScorecard"),
);
const People = React.lazy(() => import("./logged-in/execution-team/People"));
const AdminSettings = React.lazy(
  () => import("./logged-in/admin-settings/AdminSettings"),
);
const AuditLogViewer = React.lazy(
  () => import("./logged-in/audit-logs/AuditLogViewer"),
);
const Reports = React.lazy(() => import("./logged-in/reports/Reports"));
const EmployeeScorecard = React.lazy(
  () => import("./logged-in/execution-supervision/EmployeeScorecard"),
);
const PeopleView = React.lazy(
  () => import("./logged-in/execution-team-view/PeopleView"),
);
const EmployeeScorecardView = React.lazy(
  () => import("./logged-in/execution-supervision-view/EmployeeScorecardView"),
);
const PrivateRoute = React.lazy(
  () => import("./shared/functions/PrivateRoute"),
);
const LoggedOut = React.lazy(() => import("./logged-out/LoggedOut"));
const CompanyScorecards = React.lazy(
  () => import("./logged-in/company-scorecards/CompanyScorecards"),
);
const CompanyScorecard = React.lazy(
  () => import("./logged-in/company-scorecard/CompanyScorecard"),
);
const CompanyScorecardObjective = React.lazy(
  () =>
    import("./logged-in/company-scorecard-objective/CompanyScorecardObjective"),
);
const PerformanceReviews = React.lazy(
  () => import("./logged-in/performance-reviews/PerformanceReviews"),
);
const Drive = React.lazy(() => import("./logged-in/drive/Drive"));
const IndividualScorecardDraftObjective = React.lazy(
  () =>
    import("./logged-in/execution-scorecard-objective-draft/IndividualScorecardDraftObjective"),
);
const StrategyThemes = React.lazy(
  () => import("./logged-in/strategy-themes/StrategyThemes"),
);
const ReportPersonalDevelopmentPlan = React.lazy(
  () =>
    import("./logged-in/report-personal-development-plan/ReportPersonalDevelopmentPlan"),
);
const IndividualScorecardTeamLoad = React.lazy(
  () => import("./logged-in/execution-team-edit/IndividualScorecardTeamLoad"),
);
const IndividualScorecardTeamObjective = React.lazy(
  () =>
    import("./logged-in/execution-team-edit/execution-scorecard-objective-draft/IndividualScorecardTeamObjective"),
);
const CompanyStrategicMap = React.lazy(
  () =>
    import("./logged-in/company-scorecard/strategic-map/CompanyStrategicMap"),
);
const CompanyScorecardReviewView = React.lazy(
  () =>
    import("./logged-in/company-scorecard-reviews-view/CompanyScorecardReviewView"),
);
const CompanyScorecardReviews = React.lazy(
  () => import("./logged-in/company-scorecard-reviews/CompanyScorecardReviews"),
);
const LoggedIn = lazy(() => import("./logged-in/LoggedIn"));

PrimeReact.ripple = true;

const PrivateLoggedIn = () => (
  <PrivateRoute>
    <Suspense fallback={<Loading fullHeight={true} />}>
      <LoggedIn />
    </Suspense>
  </PrivateRoute>
);

const DEV_MODE_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/*Emails */}
          <Route path="home/emails" element={<Email />} />
          <Route path="home/dashboard" element={<Dashboard />} />
          {/* <Route path="home/login" element={<RegisterUserForm />} /> */}
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          {/* allow loading of other peoples scorecards start*/}
          <Route path="scorecards/people" element={<People />} />
          <Route
            path="scorecards/people/:uid"
            element={<IndividualScorecardTeamLoad />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          {/*allow loading of other peoples scorecards end */}
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />
          <Route path="reports/bonus" element={<BonusReport />} />
          {/* Bonus Report */}

          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />

          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          {/* 360 */}
          <Route path="threesixty/home/admin" element={<MainAdmin />} />
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />

          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route path="admin/settings" element={<AdminSettings />} />
          <Route path="admin/data" element={<QueryData />} />
          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};
const HR_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/*Emails */}
          <Route path="home/emails" element={<Email />} />
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          {/* allow loading of other peoples scorecards start*/}
          <Route path="scorecards/people" element={<People />} />
          <Route
            path="scorecards/people/:uid"
            element={<IndividualScorecardTeamLoad />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          {/*allow loading of other peoples scorecards end */}
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />
          <Route path="reports/bonus" element={<BonusReport />} />
          {/* Bonus Report */}

          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />

          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          {/* 360 */}
          <Route path="threesixty/home/admin" element={<MainAdmin />} />
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />

          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route path="admin/settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};

const EXECUTIVE_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="scorecards/people" element={<People />} />
          <Route path="scorecards/people/:uid" element={<PeopleView />} />
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />
          <Route path="reports/bonus" element={<BonusReport />} />

          {/* 360 */}
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />

          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};

const ADMIN_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/*Emails */}
          <Route path="home/emails" element={<Email />} />
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          {/* admin given access to company review start*/}
          <Route
            path="strategy/company-review"
            element={<CompanyScorecardReviews />}
          />
          <Route
            path="strategy/company-review/:fyid"
            element={<CompanyScorecardReviewView />}
          />
          {/* end */}
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="scorecards/people" element={<People />} />
          <Route path="scorecards/people/:uid" element={<PeopleView />} />
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />
          <Route path="reports/bonus" element={<BonusReport />} />
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          {/* 360 */}
          <Route path="threesixty/home/admin" element={<MainAdmin />} />
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />

          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route path="admin/settings" element={<AdminSettings />} />
          <Route path="admin/audit-logs" element={<AuditLogViewer />} />
          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};

const GENERAL_MANAGER_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />

          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />

          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />

          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />

          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />
          <Route path="reports/bonus" element={<BonusReport />} />

          {/* 360 */}
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />

          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};

const MANAGER_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/supervisor-dispute"
            element={<SupervisorDisputeView />}
          />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="scorecards/reviews" element={<PerformanceReviews />} />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />

          {/* 360 */}
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};
const EMPLOYEE_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />

          {/* 360 */}
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />

          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>

        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};
const GUEST_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route path="strategy/company" element={<CompanyScorecards />} />
          <Route path="strategy/company/:fyid" element={<CompanyScorecard />} />
          <Route
            path="strategy/company/:fyid/:objectiveId"
            element={<CompanyScorecardObjective />}
          />
          <Route path="scorecards/my" element={<IndividualScorecard />} />
          <Route
            path="scorecards/my/:id"
            element={<IndividualScorecardDraftObjective />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />

          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>

        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};
const BORAD_MEMBER_USER_ROUTES = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="c" element={<PrivateLoggedIn />}>
          {/* PROJECT MANAGEMENT */}
          {routes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Feedback */}
          {FeedbackRoutes.map((route: any, key: any) => {
            return (
              <Route
                key={key}
                path={route.path}
                element={<route.component />}
              />
            );
          })}
          {/* Dispute routes here*/}
          <Route path="scorecards/dispute" element={<DisputeHome />} />
          {/* feedback routes */}
          <Route
            path="personal/dashboard"
            element={<PersonalDevelopmentHome />}
          />
          <Route
            path="scorecards/dispute/:uid"
            element={<DisputeEmployeeScorecardView />}
          />
          <Route
            path="scorecards/resolve/:uid"
            element={<ResolveEmployeeDisputeScorecardView />}
          />
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route
            path="scorecards/people/:uid/:objectiveId"
            element={<IndividualScorecardTeamObjective />}
          />
          <Route path="home/dashboard" element={<Dashboard />} />
          <Route path="home/login" element={<RegisterUserForm />} />
          <Route path="home/strategy-map" element={<CompanyStrategicMap />} />
          <Route path="strategy/themes" element={<StrategyThemes />} />
          <Route
            path="strategy/company-review"
            element={<CompanyScorecardReviews />}
          />
          <Route
            path="strategy/company-review/:fyid"
            element={<CompanyScorecardReviewView />}
          />
          <Route
            path="scorecards/supervision"
            element={<EmployeeScorecard />}
          />
          <Route
            path="scorecards/supervision/:uid"
            element={<EmployeeScorecardView />}
          />
          <Route path="drive" element={<Drive />} />
          <Route path="drive/:id" element={<Drive />} />
          <Route path="reports/kpis" element={<Reports />} />
          <Route
            path="reports/development-plan"
            element={<ReportPersonalDevelopmentPlan />}
          />

          {/* 360 */}
          <Route path="threesixty/home/overview" element={<Main360 />} />
          <Route
            path="threesixty/home/feedback/reviews"
            element={<FeedbackReviewHome />}
          />
          <Route
            path="threesixty/home/feedback/reviews/:termId"
            element={<FeedbackReview360 />}
          />
          <Route
            path="threesixty/home/dashboard/set-questionnaire"
            element={<Questionnaire />}
          />
          {/* <Route
            path="threesixty/home/PrivateMessage"
            element={<PrivateMessage />}
          /> */}
          <Route
            path="questionnaire/competencies/:rateeId/:sessionId"
            element={<UserRequestsRating />}
          />
          <Route path="*" element={<Navigate to="home/dashboard" />} />
        </Route>
        <Route path="/" element={<LoggedOut />} />
        <Route path="/*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
};

const MainRoutes = observer(() => {
  const { store } = useAppContext();
  const role = store.auth.role;

  useNetwork();

  const DEV_MODE =
    !process.env.NODE_ENV || process.env.NODE_ENV === "development";

  if (DEV_MODE) return <DEV_MODE_ROUTES />;
  if (role === USER_ROLES.HR_USER) return <HR_USER_ROUTES />;
  else if (role === USER_ROLES.EXECUTIVE_USER) return <EXECUTIVE_USER_ROUTES />;
  else if (role === USER_ROLES.ADMIN_USER) return <ADMIN_USER_ROUTES />;
  else if (role === USER_ROLES.GENERAL_MANAGER)
    return <GENERAL_MANAGER_USER_ROUTES />;
  else if (role === USER_ROLES.MANAGER_USER) return <MANAGER_USER_ROUTES />;
  else if (role === USER_ROLES.BOARD_MEMBER_USER)
    return <BORAD_MEMBER_USER_ROUTES />;
  if (role === USER_ROLES.EMPLOYEE_USER) return <EMPLOYEE_USER_ROUTES />;
  return <GUEST_USER_ROUTES />;
});

const App = () => {
  const app = new MainApp();
  const { store, api, ui } = app;

  return (
    <div className="app">
      <AppContext.Provider value={{ store, api, ui }}>
        <MainRoutes />
        <SnackbarManager />
      </AppContext.Provider>
    </div>
  );
};
export default App;
